import React, { useState, useEffect } from 'react';
import { Icon, Dialog, DialogTitle, DialogContent, IconButton, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, List } from '@mui/material';
import { get, set, size } from 'global/lib/lodash';
import clsx from 'clsx';
import styles from './styles';
import { useScreen, Loader } from 'global/styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import useDebounce from 'global/hooks/useDebounce';
import { ImageList } from 'global/components/image';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { _size, scrollTop } from 'global/utils';
import { Button } from 'global/components/Button';
import NavToTop from 'feature/nav/NavToTop';
import moment from 'moment';

const DropDown = (props) => {
  return (
    <FormControl variant="standard">
      <InputLabel>{props.label}</InputLabel>
      <Select label={props.label} onChange={props.onChange}>
        <MenuItem value={undefined}>None</MenuItem>
        {props.options.map((opt) => (
          <MenuItem value={opt.id}>{opt.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const SectTypes = [
  { id: 'Best Practices', name: 'Best Practices' },
  { id: 'Quality Issues', name: 'Quality Issues' },
];

const SearchBar = () => {
  const { isMobileXS } = useScreen();
  const classes = styles({ isMobile: isMobileXS });
  const [searchVal, setSearchVal] = useState('');
  const searchDeb = useDebounce(searchVal, 1000);

  const [searchParams, setSearchParams] = useState({ useEffect: true, build: ['reportSections'], params: { page: { number: 1, size: 25 }, filter: { template: 1 }, include: 'superset,images,user' } });

  const { data, loading, meta, update } = useRetrieveData({ url: '/report-sections' }, searchParams);
  const supersets = useRetrieveData(
    { url: '/supersets' },
    {
      build: ['supersets'],
      useCache: { reload: 20 },
    }
  );

  const setParams = (selector, value) => {
    const sp = { ...searchParams };
    set(sp, 'params.page.number', 1);
    set(sp, selector, value);
    setSearchParams(sp);
  };

  const updatePage = (page) => {
    const sp = { ...searchParams };
    set(sp, 'params.page.number', page);
    setSearchParams(sp);
  };

  useEffect(() => {
    setParams('params.filter.search', searchDeb || undefined);
  }, [searchDeb]);

  return {
    data,
    meta,
    loading,
    updatePage,
    searchParams,
    update,
    Component: (
      <div className={classes.searchRow}>
        <TextField variant='standard' label={'Title or Author'} value={searchVal} onChange={(e) => setSearchVal(e.target.value)} />
        <DropDown label="System" options={supersets.data} onChange={(e) => setParams('params.filter.superset_id', e.target.value)} />
        <DropDown label="Highlight Type" options={SectTypes} onChange={(e) => setParams('params.filter.section_type', e.target.value)} />
      </div>
    ),
  };
};

const COLS = [
  { title: '', sel: 'check' },
  { title: 'Title', sel: 'title' },
  { title: 'System', sel: 'superset.name' },
  { title: 'Section Type', sel: 'sectionType' },
  { title: 'Author/Date', sel: (p) => ( `${get(p,'user.name') || ''} ${moment(get(p,'createdAt')).format('L')}` ) },
  { title: 'Text', sel: 'body', hide: (p) => p.isMobile },
  { title: 'Best Practice Photos', sel: 'images' },
];

const COLRow = (props) => {
  const { clickRow, chooseSelected, selected, item } = props;

  const { isMobileXS } = useScreen();
  const classes = styles({ isMobile: isMobileXS });

  return (
    <div onClick={(e) => clickRow(item, e)} className={clsx('COL', classes.tableRow)}>
      {COLS.map((col, _i) => {
        if (_i === 0) {
          return (
            <div className={clsx('COL', 'checkbox', classes.col, classes[`col_${_i}`])}>
              <Checkbox checked={!!get(selected, item.id)} onClick={(e) => chooseSelected(item, e)} size="small" />
            </div>
          );
        }

        const content = (() => {
          if (col.sel === 'images') {
            return <ImageList images={get(item, 'images', [])} />;
          } else if (col.sel === 'body') {
            return <div className="COL" dangerouslySetInnerHTML={{ __html: get(item, col.sel, '') }} />;
          } else if (typeof col.sel === 'function') {
            return col.sel(item);
          } else {
            return get(item, col.sel, '');
          }
        })();

        const hide = col.hide ? col.hide({ isMobile: isMobileXS }) : false;

        return hide ? null : (
          <div key={_i} className={clsx('COL', classes.col, classes.colText, classes[`col_${_i}`])}>
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default (props) => {
  const { isMobileXS } = useScreen();
  const classes = styles({ isMobile: isMobileXS });
  const [selected, setSelected] = useState({});
  const [cloning, setCloning] = useState(false);

  const { data, loading, Component, updatePage, searchParams, meta, update } = SearchBar();

  useEffect(() => {
    if (props.open) {
      update();
    } else {
      setSelected({});
    }
  }, [props.open]);

  const clickRow = (row, e) => {
    //the ImageList images are inside of the clickable div, so we are doing this check to make sure we are actually "clicking" the row
    const checks = [get(e, 'target'), get(e, 'target.offsetParent')];
    if (size(checks.filter((c) => get(c, 'className', '').indexOf('COL') > -1))) {
      get(props, 'onClick', () => 0)(row);
    }
  };

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const chooseSelected = (item, e) => {
    stopProp(e);
    const sel = { ...selected };
    if (get(sel, item.id)) {
      delete sel[item.id];
    } else {
      set(sel, item.id, item);
    }
    setSelected(sel);
  };

  const cloneAll = async () => {
    try {
      if (cloning) {
        return;
      }
      setCloning(true);
      await Promise.all(Object.keys(selected).map((key) => props.cloneItem(selected[key])));
      setCloning(false);
      props.onClose();
    } catch (err) {
      alert(`Error cloning items: ${err.message}`);
    }
  };

  const toggleAll = (e) => {
    if (!e.target.checked) {
      setSelected({});
    } else {
      const sel = {};
      data.map((it) => (sel[it.id] = true));
      setSelected(sel);
    }
  };

  return (
    <Dialog keepMounted={props.keepMounted} fullScreen={isMobileXS} className={classes.container} open={props.open} onClose={props.onClose} fullWidth={true} maxWidth="lg" id={'TemplateModal'}>
      <DialogTitle className={classes.title}>{'Add from Template'}</DialogTitle>

      <DialogContent classes={{ root: classes.dialogContent }}>
        <div className={classes.content}>
          {Component}

          {!!_size(selected) && (
            <Button className={classes.cloneBtn} startIcon={cloning && <Loader size={18} />} onClick={cloneAll}>
              Clone Report Section
            </Button>
          )}

          <div className={classes.tableContainer}>
            {!isMobileXS && (
              <div className={clsx(classes.tableHeader, classes.tableRow)}>
                {COLS.map((col, i) => (
                  <div key={i} className={clsx(classes.col, classes[`col_${i}`])} style={{marginLeft: i === 1 ? 0 : undefined}}>
                    {i === 0 ? <Checkbox onClick={toggleAll} checked={!!(!loading && !!size(data) && Object.keys(selected).length === size(data))} size={'small'} /> : col.title}
                  </div>
                ))}
              </div>
            )}
            <div className={classes.tableContent}>
              <InfiniteList
                data={data}
                loading={loading}
                selected={selected}
                chooseSelected={chooseSelected}
                clickRow={clickRow}
                handlePageChange={updatePage}
                currentPage={get(searchParams, 'params.page.number', 1)}
                lastPage={get(meta, 'page.last-page')}
              />
            </div>
            <NavToTop onClick={() => scrollTop('#TemplateModal .MuiDialogContent-root')} />
          </div>
        </div>
      </DialogContent>

      <IconButton onClick={props.onClose} className={classes.close} size="large">
        <Icon>cancel</Icon>
      </IconButton>
    </Dialog>
  );
};

const InfiniteList = withInfiniteScroll(COLRow, List);
